import {
  GraphQLServerUrl,
  RxDatabase,
  RxReplicationWriteToMasterRow,
  WithDeleted,
} from 'rxdb'
import { replicateGraphQL } from 'rxdb/plugins/replication-graphql'

import { graphql } from '@/gql'
import {
  createQueryWithHash,
  pullResponseModifier,
} from '@/system/rxdb/replications/utils.ts'
import { Assessment } from '@/system/rxdb/schemas/assessment.ts'

type PullData =
  | {
      nodes: Array<WithDeleted<Assessment>>
    }
  | { assessment: WithDeleted<Assessment> }

// amount of items we want to pull max for each iteration
const BATCH_SIZE = 20

export const ASSESSMENT_FRAGMENT = /* GraphQL*/ `
    fragment assessment on Assessment {
        rowId
        updatedAt
        createdAt
        deleted
        state
        patientId
        consultationReason
        consultationReasonOther
        patientInsuranceName
        patientInsuranceNumber
        patientInsuranceType
        patientGender
        patientTitle
        patientName
        patientSurname
        patientBirthdayAt
        patientHasCareLevel
        careServiceType
        patientCareLevel
        patientCareLevelNotes
        patientFinancialSituation
        patientAddress {
            street
            houseNumber
            postalCode
            city
        }
        patientPhoneNumber
        patientDiseases
        consultationAt
        consultationBeginAt
        consultationEndAt
        consultationParticipants
        consultationParticipantsOther
        contactPartner {
            name
            address
            phoneNumber
            healthInsuranceFund
            healthInsuranceNumber
            healthInsuranceType
        }
        contactDaughterSon {
            name
            address
            phoneNumber
            healthInsuranceFund
            healthInsuranceNumber
            healthInsuranceType
        }
        contactDaughterInLawSonInLaw {
            name
            address
            phoneNumber
            healthInsuranceFund
            healthInsuranceNumber
            healthInsuranceType
        }
        contactGrandchild {
            name
            address
            phoneNumber
            healthInsuranceFund
            healthInsuranceNumber
            healthInsuranceType
        }
        contactParent {
            name
            address
            phoneNumber
            healthInsuranceFund
            healthInsuranceNumber
            healthInsuranceType
        }
        contactFriend {
            name
            address
            phoneNumber
            healthInsuranceFund
            healthInsuranceNumber
            healthInsuranceType
        }
        contactNeighbor {
            name
            address
            phoneNumber
            healthInsuranceFund
            healthInsuranceNumber
            healthInsuranceType
        }
        contactSupervisor {
            name
            address
            phoneNumber
            healthInsuranceFund
            healthInsuranceNumber
            healthInsuranceType
        }
        contactGeneralPractitioner {
            name
            address
            phoneNumber
            healthInsuranceFund
            healthInsuranceNumber
            healthInsuranceType
        }
        contactSpecialistDoctor {
            name
            address
            phoneNumber
            healthInsuranceFund
            healthInsuranceNumber
            healthInsuranceType
        }
        contactNursingService {
            name
            address
            phoneNumber
            healthInsuranceFund
            healthInsuranceNumber
            healthInsuranceType
        }
        contactOther {
            name
            address
            phoneNumber
            healthInsuranceFund
            healthInsuranceNumber
            healthInsuranceType
        }
        supportWithCare
        supportWithCareOther
        caregiverRegistered
        medicalTreatment
        lifeForm
        lifeFormOther
        orders
        relevantContacts
        relevantContactsOther
        assignmentId
    }
`

export const createAssessmentReplication = (
  db: RxDatabase,
  url: GraphQLServerUrl,
  autoStart: boolean
) => {
  const assessmentQuery = createQueryWithHash(
    graphql(/* GraphQL */ `
      query assessmentDocuments(
        $checkpoint: DocumentCheckpointInput
        $first: Int!
      ) {
        assessmentDocuments(checkpoint: $checkpoint, first: $first) {
          nodes {
            ...assessment
          }
        }
      }
    `)
  )
  const assessmentStreamQuery = createQueryWithHash(
    graphql(/* GraphQL */ `
      subscription streamAssessment {
        streamAssessments {
          assessment {
            ...assessment
          }
        }
      }
    `)
  )
  const assessmentPushQuery = createQueryWithHash(
    graphql(/* GraphQL*/ `
      mutation addAssessment($rows: [AssessmentInput]!) {
        assessmentReplicate(input: { items: $rows }) {
          result {
            ...assessment
          }
        }
      }
    `)
  )
  return replicateGraphQL({
    collection: db.assessment,
    url,
    credentials: 'include',
    replicationIdentifier: url.http || '',
    autoStart,
    deletedField: 'deleted',
    pull: {
      includeWsHeaders: true,
      streamQueryBuilder: () => {
        return {
          ...assessmentStreamQuery,
          variables: {},
        }
      },
      responseModifier: (data: PullData, origin) => {
        // in case of subscriptions, we always only receive a single assignment, so we map it here into the right
        // expected structure
        if (origin === 'stream' && data && 'assessment' in data) {
          return pullResponseModifier({ nodes: [data.assessment] })
        }
        return pullResponseModifier<Assessment>(
          data as {
            nodes: Array<WithDeleted<Assessment>>
          }
        )
      },
      batchSize: BATCH_SIZE,
      queryBuilder: (checkpoint, limit) => {
        return {
          ...assessmentQuery,
          variables: {
            checkpoint,
            first: limit,
          },
        }
      },
    },
    push: {
      responseModifier: (data: { result: Assessment[] }) => {
        return data.result
      },
      queryBuilder(rows: RxReplicationWriteToMasterRow<Assessment>[]) {
        return {
          ...assessmentPushQuery,
          variables: {
            rows: rows.map((row) => row.newDocumentState),
          },
        }
      },
    },
  })
}
